import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/app/$propertyId/settings')({
  component: PropertySettings,
});

type PropertyDetail = {
  id: string;
  name: string;
  role: 'owner' | 'member' | null;
  defaultMowCadenceDays: number;
  archivedAt: string | null;
};

function PropertySettings() {
  const { propertyId } = Route.useParams();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () =>
      api.get<{ property: PropertyDetail }>('/v1/properties/' + propertyId),
  });

  const property = data?.property;
  const isOwner = property?.role === 'owner';

  const [name, setName] = useState('');
  const [cadence, setCadence] = useState('');

  useEffect(() => {
    if (property) {
      setName(property.name);
      setCadence(String(property.defaultMowCadenceDays));
    }
  }, [property]);

  const updateMutation = useMutation({
    mutationFn: () =>
      api.patch<{ property: PropertyDetail }>('/v1/properties/' + propertyId, {
        name: name.trim(),
        defaultMowCadenceDays: parseInt(cadence, 10) || undefined,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['property', propertyId] }),
  });

  const archiveMutation = useMutation({
    mutationFn: (archive: boolean) =>
      api.post<void>(
        '/v1/properties/' + propertyId + (archive ? '/archive' : '/unarchive'),
        {},
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['property', propertyId] }),
  });

  if (!property) return null;

  return (
    <div className="p-6 max-w-lg">
      <h2 className="text-lg font-semibold mb-6">Settings</h2>

      {/* Name + cadence */}
      <div className="rounded-lg border border-border p-5 mb-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Property name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isOwner}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Default mow cadence (days)</label>
          <input
            type="number"
            min={1}
            max={365}
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            disabled={!isOwner}
            className="w-32 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
        </div>
        {isOwner && (
          <div>
            {updateMutation.isError && (
              <p className="text-sm text-destructive mb-2">
                {updateMutation.error instanceof ApiError
                  ? updateMutation.error.message
                  : 'Save failed.'}
              </p>
            )}
            {updateMutation.isSuccess && (
              <p className="text-sm text-green-600 mb-2">Saved.</p>
            )}
            <Button
              size="sm"
              onClick={() => updateMutation.mutate()}
              disabled={!name.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </div>

      {/* Archive / unarchive — owner only */}
      {isOwner && (
        <div className="rounded-lg border border-border p-5">
          <h3 className="font-medium mb-1">
            {property.archivedAt ? 'Unarchive property' : 'Archive property'}
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            {property.archivedAt
              ? 'Restore access to this property and all its zones for you and your members.'
              : 'Hide this property from dashboards. No data is deleted. You can unarchive at any time.'}
          </p>
          {archiveMutation.isError && (
            <p className="text-sm text-destructive mb-2">
              {archiveMutation.error instanceof ApiError
                ? archiveMutation.error.message
                : 'Action failed.'}
            </p>
          )}
          <Button
            variant={property.archivedAt ? 'outline' : 'destructive'}
            size="sm"
            onClick={() => archiveMutation.mutate(!property.archivedAt)}
            disabled={archiveMutation.isPending}
          >
            {archiveMutation.isPending
              ? 'Working…'
              : property.archivedAt
                ? 'Unarchive'
                : 'Archive property'}
          </Button>
        </div>
      )}
    </div>
  );
}
